import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Loader2, ShieldAlert, Info, Sparkles, FileWarning, AlertCircle } from "lucide-react";
import { isValidPhoneNumber, countryOf } from "../lib/phone";
import PhoneInputE164 from "./PhoneInputE164";
import MultiSelectChips from "./MultiSelectChips";
import SearchableSelect from "./SearchableSelect";
import AadhaarImageInput from "./AadhaarImageInput";
import HomeTuitionLocationPicker from "./HomeTuitionLocationPicker";
import { useMasterList } from "../lib/useMasterList";
import {
    PREFERRED_DAYS, PREFERRED_TIME_WINDOWS, CLASS_DURATIONS,
    LEARNING_LEVELS, FEE_TYPES, tutorCompleteness,
} from "../lib/aiMatchFields";
import DataCompletenessMeter from "./DataCompletenessMeter";

const ALL_MAJOR_SUBJECTS = "All Major Subjects";
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PIN_RX = /^\d{6}$/;
// V26 — Mode label spec: "Home Tuition", "Live Online", "Both Home Tuition & Live Online".
const MODE_HOME = "Home Tuition";
const MODE_ONLINE = "Live Online";
const MODE_BOTH = "Both Home Tuition & Live Online";

/**
 * Tutor Application Form — V24 AI-first audit.
 *
 * V24 additions (all OPTIONAL — public form remains short for conversion):
 *   - Languages Known (multi)
 *   - Preferred Student Gender, Preferred Age Group, Max Travel km
 *   - Preferred Days / Time Slots
 *   - Class Duration Supported (multi)
 *   - Fee Expectation (min/max + type)
 *   - Demo Available toggle, Max Students Capacity, Device Ready
 *   - Exam Categories (multi), Curriculum Types (multi), Learning Support Levels
 *   - State (alongside city)
 */
export default function TutorApplicationForm({ preferCountry }) {
    // ── Master data ───────────────────────────────────────────────────────
    const { items: subjectItems } = useMasterList("subject");
    const { items: gradeItems } = useMasterList("grade");
    const { items: boardItems } = useMasterList("board");
    const { items: countryItems } = useMasterList("country");
    const { items: languageItems } = useMasterList("language");
    const { items: examItems } = useMasterList("exam_category");
    const { items: currItems } = useMasterList("curriculum_type");

    const gradeOptions = useMemo(() => gradeItems.map((g) => ({
        value: g.value,
        isPrimary: Boolean(g.extra?.is_primary),
        hint: g.extra?.is_primary ? "Primary-level — eligible for All Major Subjects" : undefined,
    })), [gradeItems]);

    const boardOptions = useMemo(() => boardItems.map((b) => ({ value: b.value })), [boardItems]);
    const countryOptions = useMemo(() => countryItems.map((c) => ({ value: c.value })), [countryItems]);

    // ── Form state ────────────────────────────────────────────────────────
    const [form, setForm] = useState({
        name: "", email: "", phone: "", qualification: "",
        experience: "", mode: MODE_HOME, gender: "",
        schedule: "", message: "",
        // V25 Batch B — mandatory at-registration enrichment (#11, #16)
        permanent_address: "", school_name: "", medium_of_schooling: "",
        aadhaar_number: "",
        // V26 — Country (default India) + mandatory PIN codes
        country: "India",
        // Home-only
        city: "", state: "", permanent_pin_code: "", max_travel_km: "",
        // Online-only
        online_availability: "", device_ready: false,
        // V24 — Teaching preferences
        preferred_student_gender: "",
        preferred_age_group: "",
        fee_expectation_min: "",
        fee_expectation_max: "",
        fee_expectation_type: "monthly",
        max_students_capacity: "",
        demo_available: true,
    });
    // V25 Batch B — Aadhaar image uploads (compressed client-side)
    const [aadhaarFront, setAadhaarFront] = useState(null);
    const [aadhaarBack, setAadhaarBack] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [grades, setGrades] = useState([]);
    const [boards, setBoards] = useState([]);
    const [teachingPins, setTeachingPins] = useState([]);
    const [homeCity, setHomeCity] = useState("");  // V31 — slug of Home Tuition city
    const [travelAreas, setTravelAreas] = useState([]);
    const [onlineCountries, setOnlineCountries] = useState([]);
    const [teachesAllMajors, setTeachesAllMajors] = useState(false);
    // V24 — new multi-select state
    const [languages, setLanguages] = useState([]);
    const [preferredDays, setPreferredDays] = useState([]);
    const [preferredTimeSlots, setPreferredTimeSlots] = useState([]);
    const [classDurations, setClassDurations] = useState([]);
    const [examCategories, setExamCategories] = useState([]);
    const [curriculumTypes, setCurriculumTypes] = useState([]);
    const [learningLevels, setLearningLevels] = useState([]);
    const [aiOpen, setAiOpen] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submittedOk, setSubmittedOk] = useState(false);
    // V26 — Per-field error state for inline validation messages
    const [errors, setErrors] = useState({});

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((er) => ({ ...er, [k]: null }));
    };
    const clearErr = (k) => () => {
        if (errors[k]) setErrors((er) => ({ ...er, [k]: null }));
    };

    const modeLower = (form.mode || "").toLowerCase();
    const showHomeFields = modeLower === MODE_HOME.toLowerCase() || modeLower === MODE_BOTH.toLowerCase();
    const showOnlineFields = modeLower === MODE_ONLINE.toLowerCase() || modeLower === MODE_BOTH.toLowerCase();

    // V30 — When the tutor switches OUT of Home/Both into a pure Live Online mode,
    // clear any previously typed Teaching Area PIN codes so they don't get
    // submitted to the database and don't reappear if the user switches back.
    useEffect(() => {
        if (!showHomeFields && teachingPins.length > 0) {
            setTeachingPins([]);
            setErrors((er) => ({ ...er, teaching_pin_code: null }));
        }
        if (!showHomeFields && homeCity) {
            setHomeCity("");
            setErrors((er) => ({ ...er, home_tuition_city: null }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showHomeFields]);

    // V24 — Live completeness % for the AI meter
    const completenessPct = useMemo(() => tutorCompleteness({
        subjects_list: subjects, grades_list: grades, boards_list: boards,
        languages, gender: form.gender, qualification: form.qualification,
        city: form.city, state: form.state,
        teaching_pin_codes: teachingPins, online_countries: onlineCountries,
        preferred_days: preferredDays, preferred_time_slots: preferredTimeSlots,
        class_duration_supported: classDurations,
        fee_expectation_min: form.fee_expectation_min || null,
        fee_expectation_max: form.fee_expectation_max || null,
        exam_categories: examCategories, curriculum_types: curriculumTypes,
        max_travel_km: form.max_travel_km || null,
        preferred_student_gender: form.preferred_student_gender,
        demo_available: form.demo_available,
        max_students_capacity: form.max_students_capacity || null,
        device_ready: form.device_ready,
    }), [subjects, grades, boards, languages, form, teachingPins, onlineCountries,
        preferredDays, preferredTimeSlots, classDurations, examCategories,
        curriculumTypes]);

    // "All Major Subjects" is offered only when at least one selected grade is primary-level
    const hasPrimaryGrade = useMemo(
        () => grades.some((g) => gradeItems.find((gi) => gi.value === g)?.extra?.is_primary),
        [grades, gradeItems],
    );

    // Wrapper around setGrades — when user removes all primary grades, drop the synthetic subject too.
    const onGradesChange = (next) => {
        const stillHasPrimary = next.some((g) => gradeItems.find((gi) => gi.value === g)?.extra?.is_primary);
        if (!stillHasPrimary && teachesAllMajors) {
            setTeachesAllMajors(false);
            setSubjects((prev) => prev.filter((s) => s !== ALL_MAJOR_SUBJECTS));
        }
        setGrades(next);
    };

    // Build the subject option list — prepend "All Major Subjects" iff a primary grade is selected
    const subjectOptions = useMemo(() => {
        const base = subjectItems.map((s) => ({ value: s.value }));
        if (hasPrimaryGrade) {
            return [
                { value: ALL_MAJOR_SUBJECTS, hint: "Covers every core subject for Pre-K → Class 8", isPrimary: true },
                ...base,
            ];
        }
        // Hide "All Major Subjects" for class 9+; also strip it from selections
        return base;
    }, [subjectItems, hasPrimaryGrade]);

    // Keep `teachesAllMajors` boolean and the synthetic subject in sync — user picks chip OR checkbox, both update each other
    const onSubjectsChange = (next) => {
        const includesAll = next.includes(ALL_MAJOR_SUBJECTS);
        setTeachesAllMajors(includesAll && hasPrimaryGrade);
        setSubjects(next);
    };

    const submit = async (e) => {
        e.preventDefault();
        // V26 — Build per-field error map (no generic popups).
        const eMap = {};
        if (!form.name.trim()) eMap.name = "Tutor Name is mandatory.";
        if (!form.email.trim()) eMap.email = "Email is mandatory.";
        else if (!EMAIL_RX.test(form.email.trim())) eMap.email = "Please enter a valid email address.";
        if (!form.phone.trim()) eMap.phone = "Phone Number is mandatory.";
        else if (!isValidPhoneNumber(form.phone)) eMap.phone = "Please enter a valid phone number.";
        if (!form.gender) eMap.gender = "Gender is mandatory.";
        if (!form.country.trim()) eMap.country = "Country is mandatory.";
        if (!form.state.trim()) eMap.state = "State is mandatory.";
        if (!form.city.trim()) eMap.city = "City is mandatory.";
        if (!form.qualification.trim()) eMap.qualification = "Qualification is mandatory.";
        if (!form.mode) eMap.mode = "Teaching Mode is mandatory.";
        if (!form.permanent_address.trim()) eMap.permanent_address = "Permanent Address is mandatory.";
        if (!form.school_name.trim()) eMap.school_name = "School / Institute Name is mandatory.";
        if (!form.medium_of_schooling) eMap.medium_of_schooling = "Medium of Schooling is mandatory.";
        if (subjects.length === 0) eMap.subjects = "Subjects to Teach are mandatory.";
        if (grades.length === 0) eMap.classes_taught = "Classes Taught are mandatory.";

        // PIN code validations
        const permPin = (form.permanent_pin_code || "").replace(/\D/g, "");
        if (!permPin) eMap.permanent_pin_code = "Permanent PIN Code is mandatory.";
        else if (!PIN_RX.test(permPin)) eMap.permanent_pin_code = "Please enter a valid 6-digit PIN Code.";
        if (!teachingPins || teachingPins.length === 0) {
            // V30 — Teaching Area PIN Codes only required for Home Tuition / Both.
            // Online-only tutors are exempt because they can teach anywhere.
            if (showHomeFields) {
                eMap.teaching_pin_code = "Teaching Area PIN Code is mandatory.";
            }
        } else {
            const bad = teachingPins.find((p) => !PIN_RX.test(String(p).replace(/\D/g, "")));
            if (bad) eMap.teaching_pin_code = `Invalid PIN code: "${bad}". Each must be 6 digits.`;
        }
        if (showHomeFields && !homeCity) {
            eMap.home_tuition_city = "Please select your Home Tuition city.";
        }

        // Aadhaar
        const aadhaarDigits = (form.aadhaar_number || "").replace(/\D/g, "");
        if (!aadhaarDigits) eMap.aadhaar_number = "Aadhaar Number is mandatory.";
        else if (aadhaarDigits.length !== 12) eMap.aadhaar_number = "Please enter a valid 12-digit Aadhaar Number.";
        if (!aadhaarFront) eMap.aadhaar_front_image = "Please upload Aadhaar Front Photo.";
        if (!aadhaarBack) eMap.aadhaar_back_image = "Please upload Aadhaar Back Photo.";

        if (showOnlineFields && onlineCountries.length === 0) {
            eMap.online_countries = "Please pick at least one country you teach online.";
        }
        if (!agreed) eMap._agreed = "Please accept the Tutor Policy & Terms before submitting.";

        setErrors(eMap);
        if (Object.keys(eMap).length > 0) {
            // Scroll the first error into view.
            const firstKey = Object.keys(eMap).find((k) => k !== "_agreed") || "_agreed";
            const el = document.querySelector(`[data-field-error-anchor="${firstKey}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            toast.error("Please fix the highlighted fields and try again.");
            return;
        }
        setSubmitting(true);
        try {
            // Strip the synthetic value from CSV for legacy field
            const subjectsCsvList = subjects.filter((s) => s !== ALL_MAJOR_SUBJECTS);
            const payload = {
                name: form.name,
                email: form.email,
                phone: form.phone,
                phone_country: countryOf(form.phone) || null,
                qualification: form.qualification,
                experience: form.experience || null,
                subjects: subjectsCsvList.join(", "),  // legacy back-compat
                subjects_list: subjects,                // new array (may include synthetic)
                grades_list: grades,
                boards_list: boards,
                mode: form.mode,
                gender: form.gender,
                country: form.country.trim(),
                classes_taught: grades.join(", ") || null,  // legacy mirror
                schedule: form.schedule || null,
                teaches_all_majors_primary: teachesAllMajors,
                // V25 Batch B mandatory enrichment
                permanent_address: form.permanent_address.trim(),
                school_name: form.school_name.trim(),
                medium_of_schooling: form.medium_of_schooling,
                aadhaar_number: aadhaarDigits,
                aadhaar_front_b64: aadhaarFront.b64,
                aadhaar_front_mime: aadhaarFront.mime,
                aadhaar_back_b64: aadhaarBack.b64,
                aadhaar_back_mime: aadhaarBack.mime,
                // Home-only
                city: form.city.trim(),
                state: form.state.trim(),
                teaching_pin_codes: showHomeFields ? teachingPins : null,
                home_tuition_city: showHomeFields ? homeCity : null,
                travel_areas: showHomeFields ? travelAreas : null,
                permanent_pin_code: showHomeFields ? (form.permanent_pin_code.trim() || null) : null,
                max_travel_km: showHomeFields && form.max_travel_km ? Number(form.max_travel_km) : null,
                // Online-only
                online_availability: showOnlineFields ? (form.online_availability.trim() || null) : null,
                online_countries: showOnlineFields ? onlineCountries : null,
                device_ready: showOnlineFields ? Boolean(form.device_ready) : null,
                // V24 — AI capability fields (all optional)
                languages: languages.length ? languages : null,
                preferred_student_gender: form.preferred_student_gender || null,
                preferred_age_group: form.preferred_age_group || null,
                preferred_days: preferredDays.length ? preferredDays : null,
                preferred_time_slots: preferredTimeSlots.length ? preferredTimeSlots : null,
                class_duration_supported: classDurations.length ? classDurations.map(Number) : null,
                fee_expectation_min: form.fee_expectation_min ? Number(form.fee_expectation_min) : null,
                fee_expectation_max: form.fee_expectation_max ? Number(form.fee_expectation_max) : null,
                fee_expectation_type: form.fee_expectation_type || null,
                demo_available: form.demo_available,
                max_students_capacity: form.max_students_capacity ? Number(form.max_students_capacity) : null,
                exam_categories: examCategories.length ? examCategories : null,
                curriculum_types: curriculumTypes.length ? curriculumTypes : null,
                learning_support_levels: learningLevels.length ? learningLevels : null,
                message: `${form.message || ""}${form.message ? "\n\n" : ""}[Tutor Policy accepted at ${new Date().toISOString()}]`,
            };
            const { data } = await api.post("/tutor-application", payload);
            toast.success(data.message);
            setSubmittedOk(true);
            try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
            setForm({
                name: "", email: "", phone: "", qualification: "",
                experience: "", mode: MODE_HOME, gender: "",
                schedule: "", message: "",
                permanent_address: "", school_name: "", medium_of_schooling: "",
                aadhaar_number: "",
                country: "India",
                city: "", state: "",
                permanent_pin_code: "", max_travel_km: "",
                online_availability: "", device_ready: false,
                preferred_student_gender: "", preferred_age_group: "",
                fee_expectation_min: "", fee_expectation_max: "",
                fee_expectation_type: "monthly",
                max_students_capacity: "", demo_available: true,
            });
            setAadhaarFront(null); setAadhaarBack(null);
            setSubjects([]); setGrades([]); setBoards([]); setTeachingPins([]); setTravelAreas([]);
            setOnlineCountries([]); setTeachesAllMajors(false);
            setLanguages([]); setPreferredDays([]); setPreferredTimeSlots([]);
            setClassDurations([]); setExamCategories([]); setCurriculumTypes([]);
            setLearningLevels([]);
            setAgreed(false);
            setErrors({});
        } catch (err) {
            // V26 — Map backend "Missing mandatory fields: a, b" → per-field errors.
            const detail = err.response?.data?.detail;
            if (typeof detail === "string" && detail.startsWith("Missing mandatory fields:")) {
                const list = detail.replace("Missing mandatory fields:", "")
                    .split(",").map((s) => s.trim()).filter(Boolean);
                const map = {};
                list.forEach((k) => { map[k] = `${tutorHumanLabel(k)} is mandatory.`; });
                setErrors(map);
            }
            toast.error(formatApiError(detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} noValidate className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-4" data-testid="tutor-form">
            {submittedOk && (
                <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 sm:p-5" data-testid="tutor-success-card">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 text-xl">✓</div>
                        <div className="flex-1 min-w-0">
                            <div className="font-heading font-bold text-emerald-900 text-base mb-1">Application received — welcome to the Anand Tutorials network!</div>
                            <p className="text-sm text-emerald-800 mb-3">
                                Your next step: read the <strong>Book My Tutor · Tutor Guide</strong> to understand the full process — profile completion, applying to tuition enquiries, demo classes, attendance, and payment policy.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <a href="/tutor-guide" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold shadow" data-testid="tutor-success-guide-cta">
                                    📘 Read Tutor Guide →
                                </a>
                                <a href="/contact" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-emerald-400 text-emerald-800 text-xs font-semibold hover:bg-emerald-100">
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <DataCompletenessMeter percent={completenessPct} kind="tutor" testid="tutor-ai-meter" />
            {/* AI Matching Disclaimer */}
            <div
                className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-[12px] leading-relaxed text-blue-900"
                data-testid="tutor-ai-disclaimer"
            >
                <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
                <div>
                    <strong>How our AI tutor matching works:</strong> Our intelligent engine recommends you to students based on the
                    subjects, grades, boards, mode and location data you provide. <strong>Tutors who pick multiple subjects, grades
                    and boards receive significantly more lead recommendations</strong>. Use the chip pickers below to add everything
                    you can teach — you can always edit later.
                </div>
            </div>

            {/* Basic identity */}
            <div className="grid sm:grid-cols-2 gap-4">
                <F label="Full Name *" error={errors.name} fieldKey="name"><input type="text" value={form.name} onChange={update("name")} data-testid="tutor-input-name" className={errors.name ? inputClsErr : inputCls} /></F>
                <F label="Email *" error={errors.email} fieldKey="email"><input type="email" value={form.email} onChange={update("email")} data-testid="tutor-input-email" className={errors.email ? inputClsErr : inputCls} /></F>
                <F label="Phone *" error={errors.phone} fieldKey="phone"><PhoneInputE164 value={form.phone} onChange={(v) => { setForm({ ...form, phone: v }); clearErr("phone")(); }} preferCountry={preferCountry} testid="tutor-input-phone" /></F>
                <F label="Gender *" error={errors.gender} fieldKey="gender">
                    <select value={form.gender} onChange={update("gender")} data-testid="tutor-input-gender" className={errors.gender ? inputClsErr : inputCls}>
                        <option value="">Select gender…</option>
                        <option value="female">Female</option>
                        <option value="male">Male</option>
                        <option value="other">Other</option>
                    </select>
                </F>
                <F label="Highest Qualification *" error={errors.qualification} fieldKey="qualification"><input type="text" value={form.qualification} onChange={update("qualification")} data-testid="tutor-input-qualification" className={errors.qualification ? inputClsErr : inputCls} placeholder="M.Sc., B.Tech, M.A. etc." /></F>
                <F label="Experience"><input type="text" value={form.experience} onChange={update("experience")} data-testid="tutor-input-experience" className={inputCls} placeholder="e.g. 5 years" /></F>
                <F label="Teaching Mode *" error={errors.mode} fieldKey="mode">
                    <select value={form.mode} onChange={update("mode")} data-testid="tutor-input-mode" className={errors.mode ? inputClsErr : inputCls}>
                        <option value="">Select mode…</option>
                        <option value={MODE_HOME}>Home Tuition</option>
                        <option value={MODE_ONLINE}>Live Online</option>
                        <option value={MODE_BOTH}>Both Home Tuition &amp; Live Online</option>
                    </select>
                </F>
                <F label="Weekly Schedule / Availability"><input type="text" value={form.schedule} onChange={update("schedule")} data-testid="tutor-input-schedule" className={inputCls} placeholder="e.g. Weekdays 5–9pm, Sat 10am–2pm" /></F>
            </div>

            {/* Grades */}
            <F label="Classes Taught *" error={errors.classes_taught} fieldKey="classes_taught">
                <MultiSelectChips
                    options={gradeOptions}
                    value={grades}
                    onChange={(v) => { onGradesChange(v); clearErr("classes_taught")(); }}
                    placeholder="Search grades (Class 1, Class 10, JEE, …)"
                    testid="tutor-grades"
                    pillTone="bg-purple-100 border-purple-400 text-purple-900"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                    ★ marks primary-level grades (Pre-K → Class 8). Selecting any of them unlocks the
                    &ldquo;All Major Subjects&rdquo; shortcut below.
                </p>
            </F>

            {/* Subjects (master-data driven, with optional All-Majors) */}
            <F label="Subjects to Teach *" error={errors.subjects} fieldKey="subjects">
                {hasPrimaryGrade && (
                    <div
                        className="flex items-start gap-2 mb-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-[12px]"
                        data-testid="tutor-allmajors-hint"
                    >
                        <Sparkles className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                        <div>
                            <strong>Primary teacher?</strong> Click the green &ldquo;✅ All Major Subjects&rdquo; chip to cover every
                            core subject (Math, Science, English, Hindi, EVS, SST…) for Pre-K → Class 8 in one go. AI will treat you
                            as eligible for any of them.
                        </div>
                    </div>
                )}
                <MultiSelectChips
                    options={subjectOptions}
                    value={subjects}
                    onChange={(v) => { onSubjectsChange(v); clearErr("subjects")(); }}
                    placeholder="Search subjects…"
                    testid="tutor-subjects"
                    pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                />
            </F>

            {/* Boards */}
            <F label="Boards / Curricula You Teach">
                <MultiSelectChips
                    options={boardOptions}
                    value={boards}
                    onChange={setBoards}
                    placeholder="Search boards (CBSE, ICSE, IB, …)"
                    testid="tutor-boards"
                    pillTone="bg-amber-100 border-amber-400 text-amber-900"
                />
            </F>

            {/* V25 Batch B — Mandatory Personal Details (always shown) */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="tutor-personal-section">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">
                    Personal Details (mandatory)
                    <span className="block text-[10px] font-normal text-slate-500 normal-case tracking-normal mt-0.5">
                        Required for verification and city-wise tuition matching.
                    </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <F label="Country *" error={errors.country} fieldKey="country">
                        <SearchableSelect
                            options={countryOptions}
                            value={form.country}
                            onChange={(v) => { setForm((f) => ({ ...f, country: v })); clearErr("country")(); }}
                            placeholder="India / USA / UAE …"
                            testid="tutor-input-country"
                            allowClear
                        />
                    </F>
                    <F label="State *" error={errors.state} fieldKey="state">
                        <input type="text" value={form.state} onChange={update("state")} data-testid="tutor-input-state" className={errors.state ? inputClsErr : inputCls} placeholder="Madhya Pradesh, Maharashtra…" />
                    </F>
                    <F label="City *" error={errors.city} fieldKey="city">
                        <input type="text" value={form.city} onChange={update("city")} data-testid="tutor-input-city" className={errors.city ? inputClsErr : inputCls} placeholder="Jabalpur, Bhopal, Nagpur…" />
                    </F>
                </div>
                <F label="Permanent Address *" error={errors.permanent_address} fieldKey="permanent_address">
                    <textarea
                        value={form.permanent_address}
                        onChange={update("permanent_address")}
                        rows={2}
                        data-testid="tutor-input-permanent-address"
                        className={`${errors.permanent_address ? inputClsErr : inputCls} resize-none`}
                        placeholder="House / Street / Area / Pincode"
                    />
                </F>
                <div className="grid sm:grid-cols-2 gap-3">
                    <F label="School / College / Institute Name *" error={errors.school_name} fieldKey="school_name">
                        <input type="text" value={form.school_name} onChange={update("school_name")} data-testid="tutor-input-school" className={errors.school_name ? inputClsErr : inputCls} placeholder="Highest educational institute" />
                    </F>
                    <F label="Medium of Schooling *" error={errors.medium_of_schooling} fieldKey="medium_of_schooling">
                        <select value={form.medium_of_schooling} onChange={update("medium_of_schooling")} data-testid="tutor-input-medium" className={errors.medium_of_schooling ? inputClsErr : inputCls}>
                            <option value="">Select medium…</option>
                            <option value="English">English</option>
                            <option value="Hindi">Hindi</option>
                            <option value="Hindi & English">Hindi &amp; English</option>
                            <option value="Other">Other</option>
                        </select>
                    </F>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <F label="Permanent PIN Code *" error={errors.permanent_pin_code} fieldKey="permanent_pin_code">
                        <input
                            type="text"
                            inputMode="numeric"
                            value={form.permanent_pin_code}
                            onChange={(e) => { setForm({ ...form, permanent_pin_code: e.target.value.replace(/\D/g, "").slice(0, 6) }); clearErr("permanent_pin_code")(); }}
                            data-testid="tutor-input-perm-pin"
                            className={errors.permanent_pin_code ? inputClsErr : inputCls}
                            placeholder="e.g. 482002"
                            maxLength={6}
                        />
                    </F>
                    {showHomeFields ? (
                        <div className="sm:col-span-1">
                            {/* V31 — City + city-scoped PIN multi-select with manual entry */}
                            <HomeTuitionLocationPicker
                                city={homeCity}
                                onCityChange={(slug) => { setHomeCity(slug); clearErr("home_tuition_city")(); }}
                                pins={teachingPins}
                                onPinsChange={(v) => {
                                    const cleaned = v.map((p) => String(p).replace(/\D/g, "")).filter((p) => p.length === 6);
                                    setTeachingPins(cleaned);
                                    clearErr("teaching_pin_code")();
                                }}
                                cityError={errors.home_tuition_city}
                                pinError={errors.teaching_pin_code}
                                testid="tutor-ht-loc"
                            />
                        </div>
                    ) : (
                        // V30 — Live Online tutors don't need teaching-area PIN codes.
                        <div
                            className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 leading-relaxed flex items-start gap-2"
                            data-testid="tutor-pins-online-note"
                        >
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                            <span>
                                <strong>You have selected Live Online mode.</strong> Since online tutors can teach
                                students from anywhere, Teaching Area PIN Codes are not required.
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* V25 Batch B — Aadhaar verification (mandatory) */}
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50/50 p-4 space-y-3" data-testid="tutor-aadhaar-section">
                <div className="flex items-start gap-2">
                    <FileWarning className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                    <div>
                        <div className="text-xs font-bold uppercase tracking-[0.15em] text-amber-900">
                            Aadhaar Verification (mandatory)
                        </div>
                        <div className="text-[11px] text-amber-800 mt-0.5">
                            Required for safety, payouts and lead access. Verified by our admin within 24 hours.
                            Your default login password will be the first 4 letters of your name (CAPS) + last 4 digits of Aadhaar.
                        </div>
                    </div>
                </div>
                <F label="Aadhaar Number *" error={errors.aadhaar_number} fieldKey="aadhaar_number">
                    <input
                        type="text"
                        value={form.aadhaar_number}
                        onChange={(e) => { setForm({ ...form, aadhaar_number: e.target.value.replace(/[^\d\s]/g, "").slice(0, 14) }); clearErr("aadhaar_number")(); }}
                        inputMode="numeric"
                        autoComplete="off"
                        data-testid="tutor-input-aadhaar"
                        className={errors.aadhaar_number ? inputClsErr : inputCls}
                        placeholder="XXXX XXXX XXXX (12 digits)"
                    />
                </F>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div data-field-error-anchor={errors.aadhaar_front_image ? "aadhaar_front_image" : undefined}>
                        <AadhaarImageInput
                            label="Aadhaar — Front Side *"
                            value={aadhaarFront}
                            onChange={(v) => { setAadhaarFront(v); if (errors.aadhaar_front_image) setErrors((er) => ({ ...er, aadhaar_front_image: null })); }}
                            testid="tutor-aadhaar-front"
                        />
                        {errors.aadhaar_front_image && (
                            <p className="mt-1 text-[12px] text-red-600 inline-flex items-center gap-1" role="alert" data-testid="tutor-error-aadhaar_front_image">
                                <AlertCircle className="w-3 h-3 shrink-0" /> {errors.aadhaar_front_image}
                            </p>
                        )}
                    </div>
                    <div data-field-error-anchor={errors.aadhaar_back_image ? "aadhaar_back_image" : undefined}>
                        <AadhaarImageInput
                            label="Aadhaar — Back Side *"
                            value={aadhaarBack}
                            onChange={(v) => { setAadhaarBack(v); if (errors.aadhaar_back_image) setErrors((er) => ({ ...er, aadhaar_back_image: null })); }}
                            testid="tutor-aadhaar-back"
                        />
                        {errors.aadhaar_back_image && (
                            <p className="mt-1 text-[12px] text-red-600 inline-flex items-center gap-1" role="alert" data-testid="tutor-error-aadhaar_back_image">
                                <AlertCircle className="w-3 h-3 shrink-0" /> {errors.aadhaar_back_image}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Home Tuition only — additional service area details */}
            {showHomeFields && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3" data-testid="tutor-home-section">
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-800">
                        Home Tuition Service Area
                        <span className="block text-[10px] font-normal text-emerald-700 normal-case tracking-normal mt-0.5">
                            Additional details to refine in-person matching (PIN codes captured above).
                        </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <F label="Max Travel Distance (km, optional)">
                            <input type="number" min="0" max="200" value={form.max_travel_km} onChange={update("max_travel_km")} data-testid="tutor-input-max-travel" className={inputCls} placeholder="e.g. 10" />
                        </F>
                    </div>
                    <F label="Areas Willing to Travel (multi — optional)">
                        <MultiSelectChips
                            options={[]}
                            value={travelAreas}
                            onChange={setTravelAreas}
                            placeholder="Type an area name and press Enter (e.g. Vijay Nagar)"
                            allowCustom
                            testid="tutor-travel-areas"
                            pillTone="bg-emerald-50 border-emerald-300 text-emerald-800"
                        />
                    </F>
                </div>
            )}

            {/* Online only — Country picker + Availability */}
            {showOnlineFields && (
                <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 space-y-3" data-testid="tutor-online-section">
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-blue-800">
                        Online Tuition Details
                        <span className="block text-[10px] font-normal text-blue-700 normal-case tracking-normal mt-0.5">
                            Pick the countries you&rsquo;d like to teach students in. Location pincodes are not required for online.
                        </span>
                    </div>
                    <F label="Countries You Teach (multi-select) *" error={errors.online_countries} fieldKey="online_countries">
                        <MultiSelectChips
                            options={countryOptions}
                            value={onlineCountries}
                            onChange={(v) => { setOnlineCountries(v); if (errors.online_countries) setErrors((er) => ({ ...er, online_countries: null })); }}
                            placeholder="Search countries…"
                            testid="tutor-online-countries"
                            pillTone="bg-blue-100 border-blue-400 text-blue-900"
                        />
                    </F>
                    <F label="Online Availability">
                        <textarea
                            value={form.online_availability}
                            onChange={update("online_availability")}
                            rows={2}
                            data-testid="tutor-input-online-availability"
                            className={`${inputCls} resize-none`}
                            placeholder="e.g. Weekdays 6–10pm IST, Sat 9am–1pm IST. NRI students UTC–5 / +4 OK."
                        />
                    </F>
                    <label className="inline-flex items-center gap-2 text-xs text-blue-900 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={form.device_ready}
                            onChange={(e) => setForm({ ...form, device_ready: e.target.checked })}
                            data-testid="tutor-input-device-ready"
                            className="w-4 h-4 accent-[#1E3A8A]"
                        />
                        I have a reliable laptop/PC, webcam, mic and stable broadband for online classes.
                    </label>
                </div>
            )}

            {/* V24 — Optional Teaching Preferences & Availability section */}
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 overflow-hidden" data-testid="tutor-ai-section">
                <button
                    type="button"
                    onClick={() => setAiOpen((v) => !v)}
                    data-testid="tutor-ai-toggle"
                    aria-expanded={aiOpen}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-emerald-100/40"
                >
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-900">
                        <Sparkles className="w-4 h-4 text-emerald-600" />
                        Teaching Preferences &amp; Availability
                        <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded">Optional — improves AI matching</span>
                    </span>
                    <span className="text-emerald-700 text-xs">{aiOpen ? "Hide" : "Show"}</span>
                </button>
                {aiOpen && (
                    <div className="px-4 pb-4 pt-1 space-y-4" data-testid="tutor-ai-fields">
                        <p className="text-[11px] text-emerald-800 -mt-1">
                            Optional — helps improve AI tutor matching accuracy. The more you share, the higher you rank in admin recommendations.
                        </p>
                        <F label="Languages You Can Teach In (optional)">
                            <MultiSelectChips
                                options={languageItems.map((l) => ({ value: l.value }))}
                                value={languages}
                                onChange={setLanguages}
                                placeholder="Search languages (English, Hindi, Tamil…)"
                                testid="tutor-input-languages"
                                pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                            />
                        </F>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <F label="Preferred Student Gender (optional)">
                                <select value={form.preferred_student_gender} onChange={update("preferred_student_gender")} data-testid="tutor-input-pref-student-gender" className={inputCls}>
                                    <option value="">Any</option>
                                    <option value="female">Female</option>
                                    <option value="male">Male</option>
                                </select>
                            </F>
                            <F label="Preferred Age Group (optional)">
                                <input type="text" value={form.preferred_age_group} onChange={update("preferred_age_group")} data-testid="tutor-input-pref-age" className={inputCls} placeholder="e.g. 10-16 yrs" />
                            </F>
                            <F label="Max Students Capacity (optional)">
                                <input type="number" min="0" max="100" value={form.max_students_capacity} onChange={update("max_students_capacity")} data-testid="tutor-input-max-students" className={inputCls} placeholder="e.g. 8" />
                            </F>
                            <F label="Demo Class Available?">
                                <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none mt-1.5">
                                    <input
                                        type="checkbox"
                                        checked={form.demo_available}
                                        onChange={(e) => setForm({ ...form, demo_available: e.target.checked })}
                                        data-testid="tutor-input-demo-available"
                                        className="w-4 h-4 accent-emerald-600"
                                    />
                                    Yes, I&apos;m happy to take a free 1:1 demo class.
                                </label>
                            </F>
                        </div>
                        <F label="Preferred Days (optional)">
                            <MultiSelectChips
                                options={PREFERRED_DAYS}
                                value={preferredDays}
                                onChange={setPreferredDays}
                                placeholder="Search days…"
                                testid="tutor-input-pref-days"
                                pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                            />
                        </F>
                        <F label="Preferred Time Slots (optional)">
                            <MultiSelectChips
                                options={PREFERRED_TIME_WINDOWS}
                                value={preferredTimeSlots}
                                onChange={setPreferredTimeSlots}
                                placeholder="Search time windows…"
                                testid="tutor-input-pref-time-slots"
                                pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                            />
                        </F>
                        <F label="Class Durations You Support (optional)">
                            <MultiSelectChips
                                options={CLASS_DURATIONS.map((d) => ({ value: String(d.value), label: `${d.value} min` }))}
                                value={classDurations.map(String)}
                                onChange={(arr) => setClassDurations(arr)}
                                placeholder="Search durations…"
                                testid="tutor-input-durations"
                                pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                            />
                        </F>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <F label="Fee Min (₹, optional)">
                                <input type="number" min="0" value={form.fee_expectation_min} onChange={update("fee_expectation_min")} data-testid="tutor-input-fee-min" className={inputCls} placeholder="e.g. 4000" />
                            </F>
                            <F label="Fee Max (₹, optional)">
                                <input type="number" min="0" value={form.fee_expectation_max} onChange={update("fee_expectation_max")} data-testid="tutor-input-fee-max" className={inputCls} placeholder="e.g. 8000" />
                            </F>
                            <F label="Fee Type">
                                <select value={form.fee_expectation_type} onChange={update("fee_expectation_type")} data-testid="tutor-input-fee-type" className={inputCls}>
                                    {FEE_TYPES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                </select>
                            </F>
                        </div>
                        <F label="Exam Categories You Can Coach (optional)">
                            <MultiSelectChips
                                options={examItems.map((e) => ({ value: e.value }))}
                                value={examCategories}
                                onChange={setExamCategories}
                                placeholder="Search exams (JEE, NEET, Boards…)"
                                testid="tutor-input-exam-categories"
                                pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                            />
                        </F>
                        <F label="Curriculum Types You Handle (optional)">
                            <MultiSelectChips
                                options={currItems.map((c) => ({ value: c.value }))}
                                value={curriculumTypes}
                                onChange={setCurriculumTypes}
                                placeholder="Search curricula (NCERT, IB-DP, Cambridge…)"
                                testid="tutor-input-curriculum"
                                pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                            />
                        </F>
                        <F label="Learning Support Levels You Handle (optional)">
                            <MultiSelectChips
                                options={LEARNING_LEVELS.map((l) => ({ value: l.value, label: l.label }))}
                                value={learningLevels}
                                onChange={setLearningLevels}
                                placeholder="Search support levels…"
                                testid="tutor-input-learning-levels"
                                pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                            />
                        </F>
                    </div>
                )}
            </div>

            <F label="Tell us about yourself">
                <textarea value={form.message} onChange={update("message")} rows={4} data-testid="tutor-input-message" className={`${inputCls} resize-none`} />
            </F>

            {/* Mandatory legal acceptance */}
            <div data-field-error-anchor={errors._agreed ? "_agreed" : undefined}>
                <label
                    className="flex items-start gap-3 p-4 border-2 border-amber-200 bg-amber-50/60 rounded-xl cursor-pointer select-none hover:border-amber-300 transition-colors"
                    data-testid="tutor-policy-consent-block"
                >
                    <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => { setAgreed(e.target.checked); if (errors._agreed) setErrors((er) => ({ ...er, _agreed: null })); }}
                        data-testid="tutor-input-agree"
                        className="mt-0.5 w-5 h-5 accent-[#1E3A8A] cursor-pointer shrink-0"
                    />
                    <span className="text-xs leading-relaxed text-slate-700">
                        <span className="inline-flex items-center gap-1.5 font-bold text-[#0F172A] uppercase tracking-[0.1em] mb-1.5">
                            <ShieldAlert className="w-3.5 h-3.5 text-amber-600" /> Mandatory Tutor Declaration
                        </span>
                        <br />
                        I have read and agree to the{" "}
                        <Link to="/policies/tutor" target="_blank" className="text-[#1E3A8A] font-semibold underline hover:text-[#1E40AF]" data-testid="tutor-policy-link">Tutor Policy</Link>,{" "}
                        <Link to="/policies/terms" target="_blank" className="text-[#1E3A8A] font-semibold underline hover:text-[#1E40AF]" data-testid="tutor-terms-link">Terms &amp; Conditions</Link>{" "}
                        and{" "}
                        <Link to="/policies/privacy" target="_blank" className="text-[#1E3A8A] font-semibold underline hover:text-[#1E40AF]" data-testid="tutor-privacy-link">Privacy Policy</Link>.
                        I understand that I am{" "}
                        <strong className="text-red-700">strictly prohibited</strong>
                        {" "}from engaging in any direct dealings with parents or students outside Anand Tutorials. I will not share my personal contact details or attempt to bypass the platform. All classes, communication and payments must be conducted only through Anand Tutorials. Any violation may result in{" "}
                        <strong className="text-red-700">immediate termination, forfeiture of pending payouts, blacklisting and necessary legal action</strong>{" "}under the Indian Contract Act, 1872 and applicable provisions of Indian law.
                    </span>
                </label>
                {errors._agreed && (
                    <p className="mt-1 text-[12px] text-red-600 inline-flex items-center gap-1" role="alert" data-testid="tutor-error-agreed">
                        <AlertCircle className="w-3 h-3 shrink-0" /> {errors._agreed}
                    </p>
                )}
            </div>

            <button
                type="submit"
                disabled={submitting}
                data-testid="tutor-submit-button"
                className="w-full bg-[#1E3A8A] text-white font-semibold py-3 rounded-lg hover:bg-[#1E40AF] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? "Submitting..." : "Submit Application"}
            </button>
        </form>
    );
}

const inputCls = "w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] transition";
const inputClsErr = "w-full px-4 py-2.5 rounded-lg border border-red-400 ring-1 ring-red-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-300 transition";

const F = ({ label, children, error, fieldKey }) => (
    <label className="block" data-field-error-anchor={error ? (fieldKey || slugifyLabel(label)) : undefined}>
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</span>
        {children}
        {error && (
            <p className="mt-1 text-[12px] text-red-600 inline-flex items-center gap-1" role="alert" data-testid={`tutor-error-${fieldKey || slugifyLabel(label)}`}>
                <AlertCircle className="w-3 h-3 shrink-0" /> {error}
            </p>
        )}
    </label>
);

function slugifyLabel(label) {
    return (label || "").toLowerCase().replace(/[*()]/g, "").trim().replace(/\s+/g, "_").replace(/[^a-z_]/g, "");
}

function tutorHumanLabel(key) {
    const map = {
        name: "Tutor Name", email: "Email", phone: "Phone Number",
        gender: "Gender", country: "Country", state: "State", city: "City",
        qualification: "Qualification", subjects: "Subjects to Teach",
        mode: "Teaching Mode", classes_taught: "Classes Taught",
        permanent_address: "Permanent Address",
        permanent_pin_code: "Permanent PIN Code",
        teaching_pin_code: "Teaching Area PIN Code",
        school_name: "School Name", medium_of_schooling: "Medium of Schooling",
        aadhaar_number: "Aadhaar Number",
        aadhaar_front_image: "Aadhaar Front Photo",
        aadhaar_back_image: "Aadhaar Back Photo",
    };
    return map[key] || key;
}
